import Vue from 'vue'
import Vuex from 'vuex'

import auth from './modules/auth'
import snackbar from './modules/snack-bar'
import storeDetail from './modules/store-detail'
import cart from './modules/cart'
import orders from './modules/orders'
import paymentMethods from './modules/payment-methods'
import menu from './modules/menu'
import menuItem from './modules/menu-item'
import customer from './modules/customer'
import checkout from './modules/checkout'
import groupOrder from './modules/group-order'
import sharedCart from './modules/shared-cart'

import { findErrorMessage } from "@/services/util.service"

import VuexPersistence from 'vuex-persist'
import VueCarousel from "vue-carousel";

// Create a safe localStorage wrapper that handles quota exceeded errors
const safeLocalStorage = {
  getItem: (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      console.error('localStorage getItem error:', e);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded. Clearing old data...');
        // Try to clear vuex state and retry
        try {
          window.localStorage.removeItem('vuex');
          window.localStorage.setItem(key, value);
        } catch (retryError) {
          console.error('Failed to save even after clearing:', retryError);
          // If still fails, clear everything except auth
          const auth = window.localStorage.getItem('auth');
          window.localStorage.clear();
          if (auth) window.localStorage.setItem('auth', auth);
        }
      } else {
        console.error('localStorage setItem error:', e);
      }
    }
  },
  removeItem: (key) => {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      console.error('localStorage removeItem error:', e);
    }
  },
  clear: () => {
    try {
      window.localStorage.clear();
    } catch (e) {
      console.error('localStorage clear error:', e);
    }
  }
};

// When the "Remember Me" option is added to Login, a session & local reducer function will be required
// https://www.npmjs.com/package/vuex-persist#reducer
let vuexLocal = new VuexPersistence ({
  storage: safeLocalStorage,
  reducer: (state) => ({
    auth: state.auth,
    cart: {
      // Only persist essential cart data to avoid quota issues
      localCart: state.cart.localCart,
      carts: state.cart.carts,
      orderType: state.cart.orderType,
      tableId: state.cart.tableId,
      // DEV-11545: the table's name (B1) lives with its id (4) -- both set/cleared together in
      // SET_TABLE_ID/CLEAR_TABLE_ID -- so a reload can name the table before the store reloads.
      tableDisplay: state.cart.tableDisplay,
      deliveryAddress: state.cart.deliveryAddress,
      // DEV-11388: coupons survive a reload like the cart they apply to. Typed codes are
      // already kept per store; the selected coupon carries the store it was applied in.
      couponCodes: state.cart.couponCodes,
      // Don't persist serverCart as it can be refetched and takes up too much space
    },
    menuItem: {
      activeCoupon: state.menuItem.activeCoupon,
      activeCouponStoreId: state.menuItem.activeCouponStoreId
    },
    customer: state.customer,
    checkout: state.checkout
  }),
  // Add error handling for quota exceeded
  strictMode: false,
  asyncStorage: false,
  supportCircular: false
})


// DEV-11388: every coupon UI selects through SET_ACTIVE_COUPON, so record the store it was
// applied in here, once -- getActiveCoupon then never applies a saved coupon to another
// store's cart (e.g. a reload straight into a different store, which the router's
// store-change reset can't see because the previous store isn't known yet).
const activeCouponStorePlugin = store => store.subscribe(mutation => {
  if (mutation.type === 'SET_ACTIVE_COUPON') {
    store.commit('SET_ACTIVE_COUPON_STORE_ID', mutation.payload ? store.getters.getUniqueStoreId : null)
  }
})

Vue.use(Vuex)
Vue.use(VueCarousel);

export default new Vuex.Store({
  plugins: [ vuexLocal.plugin, activeCouponStorePlugin ],
  modules: {
    auth,
    snackbar,
    storeDetail,
    cart,
    orders,
    paymentMethods,
    menu,
    menuItem,
    customer,
    checkout,
    groupOrder,
    sharedCart,
  },
  state: {
    isLoading: false, // This is a global loading state to be used when API calls are being made
    qrScanner: false,
    weChatDialog: false,
    menuItemDialog: false,
    couponDialog: null,
    successfulOrderDialog: false,
    orderBonusDialog: false,
    callServerDialog: false,
    deliveryDialog: false,
    pickupDeliveryEditDialog: false,
    // Raw stale scheduledTime, set when PickupDeliveryEditDialog is opened programmatically
    // (the scheduled time went stale) rather than by the user clicking the edit icon.
    // PickupDeliveryEditDialog formats it into the "no longer available" banner itself.
    pickupDeliveryEditDialogMessage: null,
    // DEV-10499: separate from pickupDeliveryEditDialogMessage on purpose -- that channel is
    // string/date-typed throughout (isStaleTimePrompt = !!message, staleScheduledTime =
    // message) and reused by several existing, carefully-commented branches. This is a plain
    // boolean flag for a different scenario entirely (submit rejected because the store
    // stopped accepting ASAP orders while the customer was checking out, not "your specific
    // scheduled time went stale") -- set right before opening the picker, shown as an inline
    // error, and cleared by the picker itself once read.
    pickupDeliveryEditDialogAsapBlocked: false,
    iframe: null,
    dialogMessage: null,
    addPaymentMethodDialog: false,
    contactTracingDialog: false
  },
  getters: {
    isLoading(state) {
      return state.isLoading
    },
    getQrScannerDialog(state) {
      return state.qrScanner
    },
    getWeChatDialog(state) {
      return state.weChatDialog
    },
    getMenuItemDialog(state) {
      return state.menuItemDialog
    },
    getCouponItemDialog: (state) => state.couponDialog,
    getSuccessfulOrderDialog(state) {
      return state.successfulOrderDialog
    },
    getOrderBonusDialog(state) {
      return !!state.orderBonusDialog
    },
    getCallServerDialog(state) {
      return !!state.callServerDialog
    },
    getDeliveryDialog(state) {
      return !!state.deliveryDialog
    },
    getPickupDeliveryEditDialog(state) {
      return !!state.pickupDeliveryEditDialog
    },
    getPickupDeliveryEditDialogMessage(state) {
      return state.pickupDeliveryEditDialogMessage
    },
    getPickupDeliveryEditDialogAsapBlocked(state) {
      return !!state.pickupDeliveryEditDialogAsapBlocked
    },
    getIframe(state) {
      return state.iframe
    },
    getDialogMessage(state) {
      return state.dialogMessage
    },
    getAddPaymentMethodDialog(state) {
      return state.addPaymentMethodDialog
    },
    getContactTracingDialog(state) {
      return state.contactTracingDialog
    }
  },
  actions: {
    setQrScanner({ commit }, value) {
      commit('updateQrScanner', value)
    },
    openDeliveryDialog({ commit }) {
      commit('SET_DELIVERY_DIALOG', true)
    },
    openPickupDeliveryEditDialog({ commit }) {
      commit('SET_PICKUP_DELIVERY_EDIT_DIALOG', true)
    },
    setLoading({ commit }, loading) {
      commit('updateLoading', loading)
    },
    closeWeChatDialog({commit}) {
      commit('CLOSE_WECHAT_DIALOG')
    },
    openWeChatDialog({commit}) {
      commit('OPEN_WECHAT_DIALOG')
    },
    closeMenuItemDialog({ commit }) {
      commit('CLOSE_MENU_ITEM_DIALOG')
    },
    openMenuItemDialog({ commit }) {
      commit('OPEN_MENU_DIALOG')
    },
    setSuccessfulOrderDialog({ commit }, val){
      commit('SET_SUCCESSFUL_ORDER_DIALOG', val)
    },

    showApiErrorResponse({ dispatch }, error) {
      dispatch('showSnack', {
        text: findErrorMessage(error),
        color: 'error'
      })
    }
  },
  mutations: {
    updateLoading(state, value) {
      state.isLoading = !!value
    },
    updateQrScanner(state, value) {
      state.qrScanner = !!value
    },
    CLOSE_WECHAT_DIALOG(state) {
      state.weChatDialog = false
    },
    OPEN_WECHAT_DIALOG(state) {
      state.weChatDialog = true
    },
    CLOSE_MENU_ITEM_DIALOG(state) {
      state.menuItemDialog = false
    },
    OPEN_MENU_DIALOG(state) {
      state.menuItemDialog = true
    },
    SET_COUPON_DIALOG(state, payload) {
      state.couponDialog = payload
    },
    SET_SUCCESSFUL_ORDER_DIALOG(state, val) {
      state.successfulOrderDialog = val
    },
    SET_ORDER_BONUS_DIALOG(state, val) {
      state.orderBonusDialog = val
    },
    SET_CALL_SERVER_DIALOG(state, val) {
      state.callServerDialog = val
    },
    SET_DELIVERY_DIALOG(state, val) {
      state.deliveryDialog = !!val
    },
    SET_PICKUP_DELIVERY_EDIT_DIALOG(state, val) {
      state.pickupDeliveryEditDialog = !!val
    },
    SET_PICKUP_DELIVERY_EDIT_DIALOG_MESSAGE(state, val) {
      state.pickupDeliveryEditDialogMessage = val
    },
    SET_PICKUP_DELIVERY_EDIT_DIALOG_ASAP_BLOCKED(state, val) {
      state.pickupDeliveryEditDialogAsapBlocked = !!val
    },
    SET_IFRAME(state, payload) {
      state.iframe = payload
    },
    SET_DIALOG_MESSAGE(state, val){
      if (!state.dialogMessage || !state.dialogMessage.immutable)
        state.dialogMessage = val
    },
    SET_ADD_PAYMENT_METHOD_DIALOG(state, val) {
      state.addPaymentMethodDialog = !!val
    },
    SET_CONTACT_TRACING_DIALOG(state, val) {
      state.contactTracingDialog = !!val
    }
  }
})
